import { useState, useEffect, useCallback } from "react"
import { styled } from "@mui/material/styles"
import { Box, Typography, IconButton, Chip, Avatar, TextField, Collapse, CircularProgress, Alert, Button, Select, MenuItem, FormControl, InputLabel, Grid } from "@mui/material"
import {
  Close as CloseIcon,
  Link as LinkIcon,
  Flag as FlagIcon,
  Assignment as AssignmentIcon,
  BugReport as BugReportIcon,
  Task as TaskIcon,
  Bookmark as BookmarkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  CallSplit as CallSplitIcon,
  Block as BlockIcon,
  OpenInNew as OpenInNewIcon,
  Remove as RemoveIcon,
  RateReview as RateReviewIcon,
  Code as CodeIcon,
  History as HistoryIcon,

} from "@mui/icons-material"
import {
  AlertTriangle as AlertTriangleIcon,
  Circle as CircleIcon
} from 'lucide-react';
import axios from "axios";
import CommentsSection from './CommentSection.jsx'
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PanelBackdrop = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(15, 23, 42, 0.4)",
  backdropFilter: "blur(6px)",
  zIndex: 1250,
  transition: "all 0.2s ease-in-out",
}));

const PanelContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: "780px",
  height: "82vh",
  backgroundColor: "#ffffff",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  borderRadius: "16px",
  zIndex: 1300,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: "1px solid #f1f5f9",
}))

const PanelHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderBottom: "1px solid #f1f5f9",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#f8fafc",
  minHeight: 64,
}))

const PanelContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2.5, 3),
}))

const StatusChip = styled(Chip)(({ status, theme }) => {
  let color = "#EBEDF0"
  let textColor = "#172B4D"

  if (status === "IN PROGRESS") {
    color = "#0052CC"
    textColor = "#FFFFFF"
  } else if (status === "REVIEW") {
    color = "#FFAB00"
    textColor = "#FFFFFF"
  } else if (status === "DONE") {
    color = "#36B37E"
    textColor = "#FFFFFF"
  }

  return {
    backgroundColor: color,
    color: textColor,
    fontWeight: 500,
    fontSize: "0.75rem",
    height: 20,
    "& .MuiChip-label": {
      paddingLeft: 8,
      paddingRight: 8,
    },
  }
})

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  padding: theme.spacing(0.5, 0),
  "&:hover": {
    backgroundColor: "#F4F5F7",
  },
  borderRadius: 4,
}))

const LinkedItemBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.5),
  border: "1px solid #DFE1E6",
  borderRadius: 4,
  marginBottom: theme.spacing(0.5),
  backgroundColor: "#FAFBFC",
  "&:hover": {
    backgroundColor: "#F4F5F7",
  },
}))

const TaskDetailsPanel = ({ issue, onClose, onUpdate, issueTypes, statuses, priorities, getInitials }) => {
  const [editing, setEditing] = useState({})
  const [userRole] = useState(localStorage.getItem("role")?.toLowerCase() || "employee");
  const [employees, setEmployees] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    people: true,
    dates: true,
    description: true,
    attachments: true,
    childIssues: true,
    linkedIssues: true,
    comments: true,
    history: false,
    shaHistory: true,
    risk: true,
  })
  const [issueDetails, setIssueDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [childIssues, setChildIssues] = useState([])
  const [linkedIssues, setLinkedIssues] = useState([])
  const [loadingChildIssues, setLoadingChildIssues] = useState(false)
  const [loadingLinkedIssues, setLoadingLinkedIssues] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
  const [shaHistory, setShaHistory] = useState([]);
  const [issueHistory, setIssueHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  useEffect(() => {
    const fetchCurrentEmployeeId = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.warn("No token found");
          return;
        }

        // Decode token ONLY to get company_id
        const payload = JSON.parse(atob(token.split(".")[1]));
        const company_id = payload.company_id;

        const response = await axios.get(
          `${API_URL}/api/user/current?company_id=${company_id}`,
          { withCredentials: true }
        );

        if (!response.data?.employee_id) {
          throw new Error("Employee ID not found in response");
        }

        setCurrentEmployeeId(response.data.employee_id);
      } catch (err) {
        //console.error("Error fetching employee ID:", err);
      }
    };

    const fetchEmployees = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(`${API_URL}/api/employees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data && Array.isArray(response.data)) {
          setEmployees(response.data);
        } else if (response.data && Array.isArray(response.data.data)) {
          setEmployees(response.data.data);
        }
      } catch (err) {
        //console.error("Error fetching employees:", err);
      }
    };

    fetchCurrentEmployeeId();
    fetchEmployees();
  }, []);

  // Get company_id from token - extracted to a function
  const getCompanyId = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found. Please log in.");
    }
    

    // Safe token parsing
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      throw new Error('Invalid token format');
    }
    
    try {
      const payload = JSON.parse(atob(tokenParts[1]));
      return payload.company_id;
    } catch (err) {
      throw new Error('Failed to parse token payload');
    }
  }, []);
const fetchIssueHistory = useCallback(async () => {
    if (!issue?.id) return;
    const token = localStorage.getItem('token');
    try {
      setLoadingHistory(true);
      const company_id = getCompanyId();
      const response = await axios.get(
        `${API_URL}/api/issues/${issue.id}/history`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data && response.data.success) {
        setIssueHistory(response.data.data || []);
      }
    } catch (err) {
      //console.error("Error fetching issue history:", err);
    } finally {
      setLoadingHistory(false);
    }
  }, [issue, getCompanyId]);

  // Call it when component mounts or issue changes
  useEffect(() => {
    fetchIssueHistory();
  }, [fetchIssueHistory]);



  // Add this function to get icon based on change type
  const getHistoryIcon = (changeType) => {
    switch(changeType) {
      case 'create':
        return <AddIcon fontSize="small" sx={{ color: "#36B37E" }} />;
      case 'status_change':
        return <FlagIcon fontSize="small" sx={{ color: "#FFAB00" }} />;
      case 'assignment_change':
        return <AssignmentIcon fontSize="small" sx={{ color: "#0052CC" }} />;
      case 'priority_change':
        return <FlagIcon fontSize="small" sx={{ color: "#FF5630" }} />;
      case 'description_change':
        return <RateReviewIcon fontSize="small" sx={{ color: "#6554C0" }} />;
      default:
        return <HistoryIcon fontSize="small" sx={{ color: "#5E6C84" }} />;
    }
  };

  // Add this function to format history message
  const formatHistoryMessage = (history) => {
    const fieldMap = {
      'summary': 'Summary',
      'description': 'Description',
      'status': 'Status',
      'priority': 'Priority',
      'severity': 'Severity',
      'assignee': 'Assignee',
      'reviewer': 'Reviewer',
      'lead': 'Lead',
      'productOwner': 'Product Owner',
      'watcher': 'Watcher',
      'startDate': 'Start Date',
      'endDate': 'End Date',
      'dueDate': 'Due Date',
      'timeEstimate': 'Time Estimate',
      'storyPoints': 'Story Points',
      'version': 'Version',
      'fixedInVersion': 'Fixed in Version',
      'foundInBuild': 'Found in Build',
      'fixedInBuild': 'Fixed in Build',
      'bugStatus': 'Bug Status',
      'attachments': 'Attachments',
      'shaId': 'SHA ID',
      'issue_created': 'Issue Created'
    };

    const fieldName = fieldMap[history.field_changed] || history.field_changed.replace(/_/g, ' ');

    if (history.change_type === 'create') {
      try {
        const data = JSON.parse(history.new_value || '{}');
        return `Created issue: ${data.summary || ''}`;
      } catch {
        return 'Issue created';
      }
    }

    if (history.field_changed === 'attachments') {
      return `Updated ${fieldName}: ${history.old_value} → ${history.new_value}`;
    }

    if (history.field_changed === 'shaId') {
      return `Added ${fieldName}: ${history.new_value}`;
    }

    // Format old/new values nicely
    const oldVal = history.old_value === 'null' || !history.old_value ? 'Not set' : history.old_value;
    const newVal = history.new_value === 'null' || !history.new_value ? 'Not set' : history.new_value;

    if (history.change_type === 'status_change') {
      return `Changed ${fieldName} from ${oldVal} → ${newVal}`;
    }

    if (history.change_type === 'assignment_change') {
      return `Reassigned ${fieldName}: ${oldVal} → ${newVal}`;
    }

    return `Updated ${fieldName}: ${oldVal} → ${newVal}`;
  };
// Fetch detailed issue data
useEffect(() => {
  const fetchIssueDetails = async () => {
    if (!issue?.id) return;
    const token = localStorage.getItem('token');
    try {
      const company_id = getCompanyId();
      setLoading(true);
      
      const response = await axios.get(`${API_URL}/api/issues/${issue.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data && response.data.success === true) {
        const issueData = response.data.data;
        
    
        
        setIssueDetails(issueData);
        
        // Process SHA IDs from issue data
        if (issueData.shaIds) {
          // Handle SHA IDs array
          const shas = Array.isArray(issueData.shaIds) ? issueData.shaIds : [];
          setShaHistory(shas);
        } else {
          setShaHistory([]);
        }
      } else {
        setIssueDetails(issue);
        setShaHistory([]);
      }
      setError(null);
    } catch (err) {
      //console.error("Error fetching issue details:", err);
      setError("Failed to load issue details: " + err.message);
      setIssueDetails(issue);
      setShaHistory([]);
    } finally {
      setLoading(false);
    }
  };

  fetchIssueDetails();
}, [issue, getCompanyId]);

  // Fetch child issues from database - FIXED URL
  useEffect(() => {
    const fetchChildIssues = async () => {
      if (!issue?.id) return
      const token = localStorage.getItem('token');
      try {
        const company_id = getCompanyId();
        setLoadingChildIssues(true)
        const response = await axios.get(`${API_URL}/api/children-issues/descendants?parent_id=${issue.id}&project_id=${issue.project}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        
        if (response.data && response.data.success === true) {
          setChildIssues(response.data.data || [])
        } else {
          setChildIssues([])
        }
      } catch (err) {
        //console.error("Error fetching child issues:", err)
        setChildIssues([])
      } finally {
        setLoadingChildIssues(false)
      }
    }

    fetchChildIssues()
  }, [issue, getCompanyId])

  // Fetch linked issues - FIXED URL and type conversion
  useEffect(() => {
    const fetchLinkedIssues = async () => {
      if (!issue?.id) return
      const token = localStorage.getItem('token');
      try {
        const company_id = getCompanyId();
        setLoadingLinkedIssues(true)
        const response = await axios.get(`${API_URL}/api/issues/${issue.id}/dependencies`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        
        if (response.data && response.data.success) {
          const processedLinkedIssues = response.data.data
            .map((dep) => {
              let displayLinkType = dep.linkType || dep.dependency_type
              let relatedIssue = null

              

              if (dep.sourceIssue && dep.sourceIssue.id === Number.parseInt(issue.id, 10)) {
                relatedIssue = dep.targetIssue
                displayLinkType = dep.linkType || dep.dependency_type
              } else if (dep.targetIssue && dep.targetIssue.id === Number.parseInt(issue.id, 10)) {
                relatedIssue = dep.sourceIssue
                const originalLinkType = dep.linkType || dep.dependency_type
                if (originalLinkType === "blocks") {
                  displayLinkType = "is blocked by"
                } else if (originalLinkType === "is blocked by") {
                  displayLinkType = "blocks"
                } else if (originalLinkType === "duplicates") {
                  displayLinkType = "is duplicated by"
                } else if (originalLinkType === "is duplicated by") {
                  displayLinkType = "duplicates"
                } else {
                  displayLinkType = originalLinkType
                }
              } else if (dep.direction) {
                relatedIssue = {
                  id: dep.id,
                  key: dep.key,
                  summary: dep.summary,
                  status: dep.status,
                  type: dep.type,
                  priority: dep.priority,
                }

                if (dep.direction === "outgoing") {
                  displayLinkType = dep.linkType || dep.dependency_type
                } else if (dep.direction === "incoming") {
                  const originalLinkType = dep.linkType || dep.dependency_type
                  if (originalLinkType === "blocks") {
                    displayLinkType = "is blocked by"
                  } else if (originalLinkType === "is blocked by") {
                    displayLinkType = "blocks"
                  } else {
                    displayLinkType = originalLinkType
                  }
                }
              }

              if (!relatedIssue) {
                console.warn("Could not determine related issue for dependency:", dep)
                return null
              }

              return {
                ...relatedIssue,
                linkType: displayLinkType,
                dependency_type: displayLinkType,
                dependencyId: dep.dependencyId || dep.id,
              }
            })
            .filter((item) => item !== null && item.id)

          setLinkedIssues(processedLinkedIssues || [])
        } else {
          setLinkedIssues([])
        }
      } catch (err) {
        //console.error("Error fetching linked issues:", err)
        setError("Failed to load linked issues: " + err.message)
        setLinkedIssues([])
      } finally {
        setLoadingLinkedIssues(false)
      }
    }

    fetchLinkedIssues()
  }, [issue, getCompanyId])

  // Handle attachments
  const currentIssue = issueDetails || issue

  const handleEdit = (field) => {
    setEditing({ ...editing, [field]: true })
  }
  
  useEffect(() => {
    if (currentIssue?.attachments) {
      setAttachments(currentIssue.attachments);
    }
  }, [currentIssue]);
  
  // useEffect(() => {
  //   if (issueDetails) {
    
  //   }
  // }, [issueDetails]);
  
  // FIXED: Attachment viewer function
  const handleViewDocument = (attachment) => {
    let fileUrl = attachment.path || attachment.filename;
    
    if (fileUrl && (fileUrl.startsWith('http://') || fileUrl.startsWith('https://'))) {
      window.open(fileUrl, "_blank");
      return;
    }
    
    // Extract just the filename if it's a full path
    if (fileUrl.includes('\\')) {
      // Handle Windows paths
      fileUrl = fileUrl.split('\\').pop();
    } else if (fileUrl.includes('/')) {
      // Handle Unix paths
      fileUrl = fileUrl.split('/').pop();
    }
    
    // Construct the correct URL
    const fullUrl = `${API_URL}/uploads/${fileUrl}`;
    
    window.open(fullUrl, "_blank");
  };

  // FIXED: Save function with better error handling
  const handleSave = async (field, value) => {
    try {
      const company_id = getCompanyId();
      const updatedIssue = { ...currentIssue, [field]: value }
      const token = localStorage.getItem("token");

      const response = await axios.put(
        `${API_URL}/api/issues/${currentIssue.id}`,
        {
          [field]: value,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && (response.data.status === "success" || response.data.success === true)) {
        const freshIssue = response.data.data || updatedIssue;
        setIssueDetails(freshIssue)
        if (onUpdate) {
          onUpdate(freshIssue)
        }
        setError(null)
      } else {
        throw new Error(response.data?.message || 'Update failed');
      }

      setEditing({ ...editing, [field]: false })
    } catch (err) {
      //console.error("Error updating issue:", err)
      setError("Failed to update issue: " + err.message)
      setEditing({ ...editing, [field]: false })
    }
  }

  const handleCancel = (field) => {
    setEditing({ ...editing, [field]: false })
  }

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    })
  }

  const handleDeleteTask = () => {
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      const company_id = getCompanyId();
      await axios.delete(`${API_URL}/api/issues/${currentIssue.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      )
      setDeleteDialogOpen(false)
      onClose()
    } catch (err) {
      //console.error("Error deleting issue:", err)
      setError("Failed to delete issue: " + err.message)
      setDeleteDialogOpen(false)
    }
  }

  const getIssueTypeIcon = (type) => {
    const typeMap = {
      Epic: <BookmarkIcon fontSize="small" sx={{ color: "#cc00c9ff" }} />,
      Story: <AssignmentIcon fontSize="small" sx={{ color: "#36B37E" }} />,
      Task: <TaskIcon fontSize="small" sx={{ color: "#0052CC" }} />,
      Bug: <BugReportIcon fontSize="small" sx={{ color: "#FF5630" }} />,
    } 
    return typeMap[type] || <TaskIcon fontSize="small" sx={{ color: "#0052CC" }} />
  }

  const getPriorityIcon = (priority) => {
    const priorityColors = {
      Highest: "#FF5630",
      High: "#FF8B00",
      Medium: "#FFAB00",
      Low: "#36B37E",
      Lowest: "#57D9A3",
    }
    return <FlagIcon fontSize="small" sx={{ color: priorityColors[priority] || "#FFAB00" }} />
  }
 const severities = [
  {
    id: 1,
    name: 'Critical',
    icon: <AlertTriangleIcon size={16} color="#e53e3e" />,
    color: '#e53e3e',
    description: 'System crashes, data loss, or major functionality broken'
  },
  {
    id: 2,
    name: 'High',
    icon: <AlertTriangleIcon size={16} color="#ed8936" />,
    color: '#ed8936',
    description: 'Major functionality impacted, workaround available'
  },
  {
    id: 3,
    name: 'Medium',
    icon: <CircleIcon size={16} color="#d69e2e" />,
    color: '#d69e2e',
    description: 'Minor functionality impacted'
  },
  {
    id: 4,
    name: 'Low',
    icon: <CircleIcon size={16} color="#38a169" />,
    color: '#38a169',
    description: 'Cosmetic issues or minor inconveniences'
  },
  {
    id: 5,
    name: 'Trivial',
    icon: <CircleIcon size={16} color="#718096" />,
    color: '#718096',
    description: 'Very minor issues with minimal impact'
  }
];
const getSeverityIcon = (severityName) => {
  const severity = severities.find(s => s.name === severityName);
  return severity?.icon || <CircleIcon size={16} color="#718096" />;
}

const getSeverityName = (severityId) => {
  if (!severities || !Array.isArray(severities)) {
    return severityId || "Unknown";
  }
  const severity = severities.find(s => s.id === Number.parseInt(severityId, 10));
  return severity?.name || severityId || "Unknown";
}
  const getLinkTypeIcon = (linkType) => {
    const linkTypeMap = {
      blocks: <BlockIcon fontSize="small" sx={{ color: "#FF5630" }} />,
      "is blocked by": <BlockIcon fontSize="small" sx={{ color: "#FF8B00" }} />,
      "relates to": <LinkIcon fontSize="small" sx={{ color: "#0052CC" }} />,
      duplicates: <CallSplitIcon fontSize="small" sx={{ color: "#36B37E" }} />,
      "is duplicated by": <CallSplitIcon fontSize="small" sx={{ color: "#57D9A3" }} />,
    }
    return linkTypeMap[linkType] || <LinkIcon fontSize="small" sx={{ color: "#0052CC" }} />
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))

      if (diffInMinutes < 1) return "just now"
      if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`
      if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)} days ago`

      return date.toLocaleDateString()
    } catch (err) {
      return "N/A"
    }
  }

  const formatShaDate = (dateString) => {
    if (!dateString) return "N/A"
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (err) {
      return "N/A"
    }
  }

  const getChildIssuesProgress = () => {
    if (childIssues.length === 0) return 0
    const completedIssues = childIssues.filter((issue) => issue.status === "DONE").length
    return Math.round((completedIssues / childIssues.length) * 100)
  }

  const handleAddChildIssue = () => {
  }

  const handleAddDependency = async () => {
  }

  // FIXED: Remove dependency function
  const handleRemoveDependency = async (linkedIssue) => {
    try {
      const company_id = getCompanyId();
      const dependencyId = linkedIssue.dependencyId
      const token = localStorage.getItem('token');
      if (!dependencyId) {
        //console.error("No dependency ID found for linked issue:", linkedIssue)
        setError("Cannot remove dependency: missing ID")
        return
      }

      await axios.delete(`${API_URL}/api/issues/dependencies/${dependencyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      )

      // Refresh linked issues
      const response = await axios.get(`${API_URL}/api/issues/${issue.id}/dependencies`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            
          }
        }
      )
      if (response.data && response.data.success) {
        const processedLinkedIssues = response.data.data
          .map((dep) => {
            let displayLinkType = dep.linkType || dep.dependency_type
            let relatedIssue = null

            if (dep.sourceIssue && dep.sourceIssue.id === Number.parseInt(issue.id, 10)) {
              relatedIssue = dep.targetIssue
              displayLinkType = dep.linkType || dep.dependency_type
            } else if (dep.targetIssue && dep.targetIssue.id === Number.parseInt(issue.id, 10)) {
              relatedIssue = dep.sourceIssue
              const originalLinkType = dep.linkType || dep.dependency_type
              if (originalLinkType === "blocks") {
                displayLinkType = "is blocked by"
              } else if (originalLinkType === "is blocked by") {
                displayLinkType = "blocks"
              } else if (originalLinkType === "duplicates") {
                displayLinkType = "is duplicated by"
              } else if (originalLinkType === "is duplicated by") {
                displayLinkType = "duplicates"
              } else {
                displayLinkType = originalLinkType
              }
            } else if (dep.direction) {
              relatedIssue = {
                id: dep.id,
                key: dep.key,
                summary: dep.summary,
                status: dep.status,
                type: dep.type,
                priority: dep.priority,
              }
              if (dep.direction === "incoming") {
                const originalLinkType = dep.linkType || dep.dependency_type
                if (originalLinkType === "blocks") {
                  displayLinkType = "is blocked by"
                } else if (originalLinkType === "is blocked by") {
                  displayLinkType = "blocks"
                } else {
                  displayLinkType = originalLinkType
                }
              } else {
                displayLinkType = dep.linkType || dep.dependency_type
              }
            }

            if (!relatedIssue) return null

            return {
              ...relatedIssue,
              linkType: displayLinkType,
              dependency_type: displayLinkType,
              dependencyId: dep.dependencyId || dep.id,
            }
          })
          .filter((item) => item !== null && item.id)

        setLinkedIssues(processedLinkedIssues || [])
        setError(null)
      }
    } catch (err) {
      //console.error("Error removing dependency:", err)
      setError("Failed to remove dependency: " + err.message)
    }
  }

  const getStatusName = (statusId) => {
    if (!statuses || !Array.isArray(statuses)) {
      return statusId || "Unknown"
    }
    const status = statuses.find((s) => s.id === Number.parseInt(statusId, 10))
    return status?.name || statusId || "Unknown"
  }

  const getPriorityName = (priorityId) => {
    if (!priorities || !Array.isArray(priorities)) {
      return priorityId || "Unknown"
    }
    const priority = priorities.find((p) => p.id === Number.parseInt(priorityId, 10))
    return priority?.name || priorityId || "Unknown"
  }

  const getTypeName = (typeId) => {
    if (!issueTypes || !Array.isArray(issueTypes)) {
      return typeId || "Unknown"
    }
    const type = issueTypes.find((t) => t.id === Number.parseInt(typeId, 10))
    return type?.name || typeId || "Unknown"
  }
  const getCurrentShaId = () => {
    try {
      // First check shaHistory array
      if (shaHistory && Array.isArray(shaHistory) && shaHistory.length > 0) {
        const mostRecentSha = shaHistory[shaHistory.length - 1];
        
        // Handle object format
        if (typeof mostRecentSha === 'object' && mostRecentSha !== null) {
          return mostRecentSha.id || mostRecentSha.shaId || null;
        } 
        // Handle string format
        else if (typeof mostRecentSha === 'string') {
          return mostRecentSha;
        }
      }
      
      // Then check currentIssue
      if (currentIssue) {
        // Check currentIssue.shaId (single field)
        if (currentIssue.shaId && typeof currentIssue.shaId === 'string') {
          return currentIssue.shaId;
        }
        
        // Check currentIssue.shaIds array
        if (currentIssue.shaIds && Array.isArray(currentIssue.shaIds) && currentIssue.shaIds.length > 0) {
          const firstSha = currentIssue.shaIds[0];
          if (typeof firstSha === 'object' && firstSha !== null) {
            return firstSha.id || firstSha.shaId || null;
          } else if (typeof firstSha === 'string') {
            return firstSha;
          }
        }
      }
      
      return null;
    } catch (error) {
      //console.error("Error in getCurrentShaId:", error);
      return null;
    }
  };
  const getFormattedShaHistory = () => {
    if (!shaHistory || !Array.isArray(shaHistory)) {
      return [];
    }
    
    return shaHistory.map((sha, index) => {
      let shaId = '';
      let timestamp = new Date().toISOString();
      
      if (typeof sha === 'string') {
        shaId = sha;
      } else if (typeof sha === 'object' && sha !== null) {
        shaId = sha.id || sha.shaId || '';
        timestamp = sha.timestamp || sha.addedAt || new Date().toISOString();
      }
      
      return {
        id: shaId, // Keep full ID
        timestamp: timestamp
      };
    }).filter(sha => sha.id); // Filter out empty IDs
  };

  const renderFormattedDescription = (text) => {
    if (!text) return <Typography variant="body2" color="text.secondary">Click to add description</Typography>;
    
    if (!text.includes('**Story:**') && !text.includes('**Description:**') && !text.includes('**Acceptance Criteria:**') && !text.includes('**Definition of Done:**')) {
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', fontSize: '0.8rem' }}>
          {text}
        </Typography>
      );
    }
    
    const lines = text.split('\n');
    const elements = [];
    let currentBlock = [];
    let currentHeader = null;
    
    const commitBlock = (header, blockLines, index) => {
      if (blockLines.length === 0 && !header) return;
      
      const contentText = blockLines.join('\n').trim();
      if (!contentText && header) return;
      
      if (header) {
        const cleanHeader = header.replace(/\*\*/g, '').replace(/:/g, '').trim();
        let headerColor = "primary.main";
        let bgcolor = "transparent";
        let borderLeft = "none";
        let padding = 0;
        let mt = 1.5;
        
        if (cleanHeader === "Story") {
          headerColor = "secondary.main";
          bgcolor = "#faf6fb";
          borderLeft = "3px solid #9c27b0";
          padding = 1.5;
          mt = 0.5;
        } else if (cleanHeader === "Description") {
          headerColor = "primary.dark";
          borderLeft = "3px solid #1976d2";
          bgcolor = "#f4f8fb";
          padding = 1.5;
        } else if (cleanHeader === "Acceptance Criteria") {
          headerColor = "success.main";
        } else if (cleanHeader === "Definition of Done") {
          headerColor = "info.main";
        }
        
        elements.push(
          <Box key={`block-${index}`} sx={{ mt, mb: 1.5, p: padding, bgcolor, borderLeft, borderRadius: 1 }}>
            <Typography variant="caption" fontWeight="bold" color={headerColor} sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, letterSpacing: 0.5 }}>
              {cleanHeader}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>
              {contentText}
            </Typography>
          </Box>
        );
      } else {
        elements.push(
          <Typography key={`block-${index}`} variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-line', mb: 1.5, fontSize: '0.8rem' }}>
            {contentText}
          </Typography>
        );
      }
    };
    
    let blockIndex = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const match = line.trim().match(/^\*\*(.*?)\*\*:/);
      if (match) {
        commitBlock(currentHeader, currentBlock, blockIndex++);
        currentHeader = match[0];
        currentBlock = [];
      } else {
        currentBlock.push(line);
      }
    }
    commitBlock(currentHeader, currentBlock, blockIndex++);
    
    return <Box sx={{ mt: 0.5 }}>{elements}</Box>;
  };

  return (
    <>
      <PanelBackdrop onClick={onClose} />
      <PanelContainer>
        <PanelHeader>
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            {getIssueTypeIcon(getTypeName(currentIssue.issueType))}
            <Typography variant="subtitle2" sx={{ ml: 1, mr: 2, fontSize: "0.9rem" }}>
              {currentIssue.key}
            </Typography>
            <StatusChip status={getStatusName(currentIssue.status)} label={getStatusName(currentIssue.status)} size="small" />
          </Box>
          <Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </PanelHeader>

        <PanelContent>
          {error && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: "0.8rem" }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Task Title */}
          <Box sx={{ mb: 2 }}>
            {editing.summary ? (
              <TextField
                fullWidth
                multiline
                rows={2}
                defaultValue={currentIssue.summary}
                variant="outlined"
                size="small"
                autoFocus
                onBlur={(e) => handleSave("summary", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    handleSave("summary", e.target.value)
                  } else if (e.key === "Escape") {
                    handleCancel("summary")
                  }
                }}
              />
            ) : (
              <Typography
                variant="h6"
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#F4F5F7" },
                  p: 1,
                  borderRadius: 1,
                  fontSize: "1rem",
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
                onClick={() => handleEdit("summary")}
              >
                {currentIssue.summary}
              </Typography>
            )}
          </Box>
  
          {/* Description Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("description")}>
              {expandedSections.description ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600, fontSize: "0.85rem" }}>
                Description
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.description}>
              <Box sx={{ pl: 3, pb: 1 }}>
                {editing.description ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    defaultValue={currentIssue.description || ""}
                    variant="outlined"
                    size="small"
                    autoFocus
                    onBlur={(e) => handleSave("description", e.target.value)}
                  />
                ) : (
                  <Box
                    sx={{
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#F4F5F7" },
                      p: 1,
                      borderRadius: 1,
                      minHeight: 40,
                    }}
                    onClick={() => handleEdit("description")}
                  >
                    {renderFormattedDescription(currentIssue.description)}
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
          
          {/* Details Section */}
       <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("details")}>
              {expandedSections.details ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600, fontSize: "0.85rem" }}>
                Details
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.details}>
              <Box sx={{ pl: 3, pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
                    Status:
                  </Typography>
                  <StatusChip
                    status={getStatusName(currentIssue.status)}
                    label={getStatusName(currentIssue.status)}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
                    Priority:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {getPriorityIcon(getPriorityName(currentIssue.priority))}
                    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
                      {getPriorityName(currentIssue.priority)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
                    severity:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {getSeverityIcon(getSeverityName(currentIssue.severity))}
                    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
                      {getSeverityName(currentIssue.severity)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
                    Type:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {getIssueTypeIcon(getTypeName(currentIssue.issueType))}
                    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
                      {getTypeName(currentIssue.issueType)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
                    Story Points:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
                      {currentIssue.storyPoints ?  `${currentIssue.storyPoints} pts` : "0 pts"}
                    </Typography>
                  </Box>
                </Box>
                {/* SHA ID in Details */}
              
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
    Fixed in SHA:
  </Typography>
  <Box sx={{ display: "flex", alignItems: "center", flexWrap: 'wrap', maxWidth: '400px' }}>
    <CodeIcon fontSize="small" sx={{ color: "#0052CC", mr: 0.5 }} />
    <Typography 
      variant="body2" 
      sx={{ 
        fontSize: "0.8rem", 
        fontFamily: 'monospace',
        color: "#172B4D",
        wordBreak: 'break-all'  // This allows long IDs to wrap
      }}
    >
      {getCurrentShaId() !== "N/A" ? getCurrentShaId() : "N/A"}
    </Typography>
  </Box>
</Box>
           {/* Version (Found in Version) */}
<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
    Found in Version:
  </Typography>
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
      {currentIssue.version || "N/A"}
    </Typography>
  </Box>
</Box>
<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
    Found in Build:
  </Typography>
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
      {currentIssue.foundInBuild || "N/A"}
    </Typography>
  </Box>
</Box>
{/* Fixed in Version */}
<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
    Fixed in Version:
  </Typography>
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
      {currentIssue.fixedInVersion || "N/A"}
    </Typography>
  </Box>
</Box>   
<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70, fontSize: "0.8rem" }}>
    Fixed in Build:
  </Typography>
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Typography variant="body2" sx={{ ml: 1, fontSize: "0.8rem" }}>
      {currentIssue.fixedInBuild || "N/A"}
    </Typography>
  </Box>
</Box>
              </Box>
            </Collapse>
          </Box>

          {/* People Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("people")}>
              {expandedSections.people ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600, fontSize: "0.85rem" }}>
                People
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.people}>
              <Box sx={{ pl: 3, pb: 1 }}>
                {/* Assignee */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, fontSize: "0.8rem" }}>
                    Assignee:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ width: 16, height: 16, fontSize: "0.6rem", mr: 1 }}>
                      {getInitials(currentIssue.assigneeName || currentIssue.assignee || "")}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {currentIssue.assigneeName || currentIssue.assignee || "Unassigned"}
                    </Typography>
                  </Box>
                </Box>

                {/* Reviewer */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, fontSize: "0.8rem" }}>
                    Reviewer:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ width: 16, height: 16, fontSize: "0.6rem", mr: 1 }}>
                      {getInitials(currentIssue.reviewerName || currentIssue.reviewer || "")}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {currentIssue.reviewerName || currentIssue.reviewer || "Unassigned"}
                    </Typography>
                  </Box>
                </Box>
                 {/* lead */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, fontSize: "0.8rem" }}>
                    Lead:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ width: 16, height: 16, fontSize: "0.6rem", mr: 1 }}>
                      {getInitials(currentIssue.leadName || currentIssue.lead || "")}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {currentIssue.leadName || currentIssue.lead || "Unassigned"}
                    </Typography>
                  </Box>
                </Box>

                {/* Product Owner */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, fontSize: "0.8rem" }}>
                    Product Owner:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    {currentIssue.productOwnerName ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ width: 16, height: 16, fontSize: "0.6rem", mr: 1 }}>
                          {getInitials(currentIssue.productOwnerName)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                          {currentIssue.productOwnerName}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                        Not assigned
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Watchers */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 100, fontSize: "0.8rem", mt: 0.5 }}
                  >
                    Reporter:
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {currentIssue.reporterName && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ width: 16, height: 16, fontSize: "0.6rem", mr: 1 }}>
                          {getInitials(currentIssue.reporterName)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                          {currentIssue.reporterName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 100, fontSize: "0.8rem", mt: 0.5 }}
                  >
                    Watchers:
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {currentIssue.watcherName ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ width: 16, height: 16, fontSize: "0.6rem", mr: 1 }}>
                          {getInitials(currentIssue.watcherName)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                          {currentIssue.watcherName}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                        No watchers
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* Dates Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("dates")}>
              {expandedSections.dates ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600, fontSize: "0.85rem" }}>
                Dates
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.dates}>
            
              <Box sx={{ pl: 3, pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontSize: "0.8rem" }}>
                    Created Date:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {currentIssue.created || currentIssue.created 
                      ? new Date(currentIssue.created || currentIssue.created).toLocaleDateString('en-GB')
                      : "Not set"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontSize: "0.8rem" }}>
                    Start Date:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {currentIssue.start_date || currentIssue.startDate 
                      ? new Date(currentIssue.start_date || currentIssue.startDate).toLocaleDateString('en-GB')
                      : "Not set"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontSize: "0.8rem" }}>
                    Due Date:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {currentIssue.due_date || currentIssue.endDate || currentIssue.dueDate
                      ? new Date(currentIssue.due_date || currentIssue.endDate || currentIssue.dueDate).toLocaleDateString('en-GB')
                      : "Not set"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontSize: "0.8rem" }}>
                    Completion Date:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {currentIssue.completiondate 
                      ? new Date(currentIssue.completiondate).toLocaleDateString('en-GB')
                      : "-"}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* SHA History Section - NEW */}
          
          <Box sx={{ mb: 2 }}>
  <SectionHeader onClick={() => toggleSection("shaHistory")}>
    {expandedSections.shaHistory ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
    <HistoryIcon fontSize="small" sx={{ ml: 1, mr: 1, color: "#0052CC" }} />
    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.85rem", flex: 1 }}>
      SHA History
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", mr: 1 }}>
      ({shaHistory.length})
    </Typography>
  </SectionHeader>
  <Collapse in={expandedSections.shaHistory}>
    <Box sx={{ pl: 3, pb: 1 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          {getFormattedShaHistory().length > 0 ? (
            getFormattedShaHistory().map((sha, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: "flex", 
                  alignItems: "flex-start", 
                  padding: "8px",
                  border: "1px solid #DFE1E6",
                  borderRadius: 1,
                  marginBottom: 1,
                  backgroundColor: "#FAFBFC",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                  <CodeIcon fontSize="small" sx={{ color: "#0052CC", mr: 1, mt: 0.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}> {/* minWidth: 0 allows text to wrap */}
                    {/* Show FULL SHA ID */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: "0.8rem", 
                        fontWeight: 500, 
                        fontFamily: 'monospace',
                        color: "#172B4D",
                        wordBreak: 'break-all' // This makes long SHA IDs wrap
                      }}
                    >
                      {sha.id || "N/A"}
                    </Typography>
                    {/* Show date */}
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {formatShaDate(sha.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem", fontStyle: "italic", textAlign: "center", py: 2 }}
            >
              No SHA history
            </Typography>
          )}
        </>
      )}
    </Box>
  </Collapse>
</Box>

          {/* Attachments Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("attachments")}>
              {expandedSections.attachments ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
              <Typography
                variant="subtitle2"
                sx={{ ml: 1, fontWeight: 600, fontSize: "0.85rem" }}
              >
                Attachments
              </Typography>
            </SectionHeader>

            <Collapse in={expandedSections.attachments}>
              <Box sx={{ pl: 3, pb: 1 }}>
                {loadingAttachments ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <>
                    {Array.isArray(attachments) && attachments.length > 0 ? (
                      attachments.map((attachment) => (
                        <LinkedItemBox key={attachment.id}>
                          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                            <AssignmentIcon fontSize="small" sx={{ color: "#0052CC" }} />
                            <Box sx={{ ml: 1, flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                              >
                                {attachment.name || attachment.filename}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                {attachment.size
                                  ? `${(attachment.size / 1024).toFixed(2)} KB`
                                  : "Unknown size"}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDocument(attachment)}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </LinkedItemBox>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem", fontStyle: "italic" }}
                      >
                        No attachments
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Collapse>
          </Box>
<Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("history")}>
              {expandedSections.history ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <HistoryIcon fontSize="small" sx={{ ml: 1, mr: 1, color: "#0052CC" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.85rem", flex: 1 }}>
                Activity History
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", mr: 1 }}>
                ({issueHistory.length})
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.history}>
              <Box sx={{ pl: 3, pb: 1 }}>
                {loadingHistory ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <>
                    {issueHistory.length > 0 ? (
                      issueHistory.map((historyItem, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            display: "flex", 
                            alignItems: "flex-start", 
                            padding: "8px",
                            border: "1px solid #DFE1E6",
                            borderRadius: 1,
                            marginBottom: 1,
                            backgroundColor: "#FAFBFC",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                            {getHistoryIcon(historyItem.change_type)}
                            <Box sx={{ ml: 1, flex: 1 }}>
                              {/* Change description */}
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: "0.8rem", 
                                  fontWeight: 500,
                                  color: "#172B4D",
                                }}
                              >
                                {formatHistoryMessage(historyItem)}
                              </Typography>
                              {/* Who and when */}
                              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                                {/* <Avatar sx={{ width: 16, height: 16, fontSize: "0.5rem", mr: 0.5 }}>
                                  {getInitials(historyItem.employee_name)}
                                </Avatar>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ fontSize: "0.7rem", mr: 1 }}
                                >
                                  {historyItem.employee_name}
                                </Typography> */}
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ fontSize: "0.7rem" }}
                                >
                                  {formatDate(historyItem.created_at)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem", fontStyle: "italic", textAlign: "center", py: 2 }}
                      >
                        No activity history
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Collapse>
          </Box>
          {/* Linked Issues Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("linkedIssues")}>
              {expandedSections.linkedIssues ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
              <LinkIcon fontSize="small" sx={{ ml: 1, mr: 1, color: "#0052CC" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.85rem", flex: 1 }}>
                Dependencies
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.linkedIssues}>
              <Box sx={{ pl: 3, pb: 1 }}>
                {loadingLinkedIssues ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <>
                    {linkedIssues.map((linkedIssue) => (
                      <LinkedItemBox key={linkedIssue.id}>
                        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                          {getLinkTypeIcon(linkedIssue.linkType || linkedIssue.dependency_type)}
                          <Box sx={{ ml: 1, flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                              {linkedIssue.linkType || linkedIssue.dependency_type}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {getIssueTypeIcon(linkedIssue.type)}
                              <Typography variant="body2" sx={{ ml: 0.5, fontSize: "0.8rem", fontWeight: 500 }}>
                                {linkedIssue.key}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                              {linkedIssue.summary}
                            </Typography>
                          </Box>
                          <StatusChip
                            status={getStatusName(linkedIssue.status)}
                            label={getStatusName(linkedIssue.status)}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                          <IconButton size="small" onClick={() => handleRemoveDependency(linkedIssue)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </LinkedItemBox>
                    ))}
                    {linkedIssues.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem", fontStyle: "italic" }}
                      >
                        No linked work items
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Collapse>
          </Box>

          {/* Child Issues Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("childIssues")}>
              {expandedSections.childIssues ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
              <AssignmentIcon fontSize="small" sx={{ ml: 1, mr: 1, color: "#0052CC" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.85rem", flex: 1 }}>
                Child Issues
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", mr: 1 }}>
                ({childIssues.length})
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.childIssues}>
              <Box sx={{ pl: 3, pb: 1 }}>
                {loadingChildIssues ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <>
                    {childIssues.map((childIssue) => (
                      <Box 
                        key={childIssue.id}
                        sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          padding: "8px",
                          border: "1px solid #DFE1E6",
                          borderRadius: 1,
                          marginBottom: 1,
                          backgroundColor: "#FAFBFC",
                        }}
                      >
                        {/* Issue Type Icon */}
                        {getIssueTypeIcon(getTypeName(childIssue.issueType))}
                        
                        {/* Key */}
                        <Typography variant="body2" sx={{ ml: 1, mr: 2, fontWeight: 500, minWidth: 60, fontSize: "0.8rem" }}>
                          {childIssue.key}
                        </Typography>
                        
                        {/* Status */}
                        <StatusChip
                          status={getStatusName(childIssue.status)}
                          label={getStatusName(childIssue.status)}
                          size="small"
                          sx={{ mr: 2 }}
                        />
                        
                        {/* Summary */}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: "0.8rem",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {childIssue.summary}
                        </Typography>
                      </Box>
                    ))}
                    {childIssues.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem", fontStyle: "italic", textAlign: "center", py: 2 }}
                      >
                        No child issues
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Collapse>
          </Box>

          {/* Risk Management Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("risk")}>
              {expandedSections.risk ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <AlertTriangleIcon size={16} style={{ marginLeft: 8, marginRight: 8, color: "#FF8B00" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.85rem", flex: 1 }}>
                Risk Management
              </Typography>
              {currentIssue.risk_status && currentIssue.risk_status !== "None" && (
                <Chip 
                  label={currentIssue.risk_status} 
                  size="small" 
                  color={
                    currentIssue.risk_status === "Occurred" ? "error" :
                    currentIssue.risk_status === "Mitigating" ? "primary" :
                    currentIssue.risk_status === "Resolved" ? "success" : "warning"
                  }
                  sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                />
              )}
            </SectionHeader>
            <Collapse in={expandedSections.risk}>
              <Box sx={{ pl: 3, pb: 1, pt: 1 }}>
                {!currentIssue.risk_status || currentIssue.risk_status === "None" ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: "0.8rem", fontStyle: "italic" }}>
                      No active risk is currently raised for this task.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AlertTriangleIcon size={14} />}
                      onClick={() => {
                        handleSave("risk_status", "Identified");
                        handleSave("risk_probability", "Medium");
                        handleSave("risk_impact", "Medium");
                        if (currentIssue.projectDetails?.product_owner_id) {
                          handleSave("risk_owner", currentIssue.projectDetails.product_owner_id);
                        }
                      }}
                      sx={{
                        textTransform: "none",
                        bgcolor: "#FF8B00",
                        "&:hover": { bgcolor: "#E07B00" },
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        borderRadius: 1.5
                      }}
                    >
                      Raise a Risk
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Grid container spacing={2}>
                      {/* Risk Status */}
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Risk Status
                        </Typography>
                        {["manager", "admin", "scrum master", "project manager", "product owner"].includes(userRole) ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={currentIssue.risk_status || "Identified"}
                              onChange={(e) => handleSave("risk_status", e.target.value)}
                              sx={{ fontSize: "0.8rem", height: 32 }}
                            >
                              <MenuItem value="None" sx={{ fontSize: "0.8rem" }}>None (Clear/Close)</MenuItem>
                              <MenuItem value="Identified" sx={{ fontSize: "0.8rem" }}>Identified</MenuItem>
                              <MenuItem value="Mitigating" sx={{ fontSize: "0.8rem" }}>Mitigating</MenuItem>
                              <MenuItem value="Occurred" sx={{ fontSize: "0.8rem" }}>Occurred</MenuItem>
                              <MenuItem value="Resolved" sx={{ fontSize: "0.8rem" }}>Resolved</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip 
                            label={currentIssue.risk_status} 
                            size="small" 
                            color={
                              currentIssue.risk_status === "Occurred" ? "error" :
                              currentIssue.risk_status === "Mitigating" ? "primary" :
                              currentIssue.risk_status === "Resolved" ? "success" : "warning"
                            }
                            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                          />
                        )}
                      </Grid>

                      {/* Probability */}
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Probability
                        </Typography>
                        {(["manager", "admin", "scrum master", "project manager", "product owner"].includes(userRole) || currentIssue.risk_status === "Identified") ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={currentIssue.risk_probability || "Medium"}
                              onChange={(e) => handleSave("risk_probability", e.target.value)}
                              sx={{ fontSize: "0.8rem", height: 32 }}
                            >
                              <MenuItem value="High" sx={{ fontSize: "0.8rem" }}>High</MenuItem>
                              <MenuItem value="Medium" sx={{ fontSize: "0.8rem" }}>Medium</MenuItem>
                              <MenuItem value="Low" sx={{ fontSize: "0.8rem" }}>Low</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                            {currentIssue.risk_probability || "Medium"}
                          </Typography>
                        )}
                      </Grid>

                      {/* Impact */}
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Impact
                        </Typography>
                        {(["manager", "admin", "scrum master", "project manager", "product owner"].includes(userRole) || currentIssue.risk_status === "Identified") ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={currentIssue.risk_impact || "Medium"}
                              onChange={(e) => handleSave("risk_impact", e.target.value)}
                              sx={{ fontSize: "0.8rem", height: 32 }}
                            >
                              <MenuItem value="High" sx={{ fontSize: "0.8rem" }}>High</MenuItem>
                              <MenuItem value="Medium" sx={{ fontSize: "0.8rem" }}>Medium</MenuItem>
                              <MenuItem value="Low" sx={{ fontSize: "0.8rem" }}>Low</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                            {currentIssue.risk_impact || "Medium"}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>

                    {/* Risk Reason / Root Cause */}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Risk Reason / Root Cause
                      </Typography>
                      {(["manager", "admin", "scrum master", "project manager", "product owner"].includes(userRole) || currentIssue.risk_status === "Identified") ? (
                        editing.risk_reason ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            defaultValue={currentIssue.risk_reason || ""}
                            variant="outlined"
                            size="small"
                            autoFocus
                            onBlur={(e) => handleSave("risk_reason", e.target.value)}
                          />
                        ) : (
                          <Box
                            sx={{
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#F4F5F7" },
                              p: 1,
                              border: "1px dashed #DFE1E6",
                              borderRadius: 1,
                              minHeight: 40,
                            }}
                            onClick={() => handleEdit("risk_reason")}
                          >
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: currentIssue.risk_reason ? "text.primary" : "text.secondary" }}>
                              {currentIssue.risk_reason || "Click to add root cause description..."}
                            </Typography>
                          </Box>
                        )
                      ) : (
                        <Box sx={{ p: 1, bgcolor: "#FAFBFC", border: "1px solid #DFE1E6", borderRadius: 1, minHeight: 40 }}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem", color: currentIssue.risk_reason ? "text.primary" : "text.secondary" }}>
                            {currentIssue.risk_reason || "No root cause logged."}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Mitigation Owner */}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Mitigation Owner
                      </Typography>
                      {["manager", "admin", "scrum master", "project manager", "product owner"].includes(userRole) ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={currentIssue.risk_owner || ""}
                            onChange={(e) => handleSave("risk_owner", e.target.value)}
                            sx={{ fontSize: "0.8rem", height: 32 }}
                            displayEmpty
                          >
                            <MenuItem value="" sx={{ fontSize: "0.8rem" }}>Unassigned</MenuItem>
                            {employees.map((emp) => (
                              <MenuItem key={emp.employee_id} value={emp.employee_id} sx={{ fontSize: "0.8rem" }}>
                                {emp.firstName} {emp.lastName} ({emp.employee_id})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Box sx={{ p: 1, bgcolor: "#FAFBFC", border: "1px solid #DFE1E6", borderRadius: 1, minHeight: 32, display: "flex", alignItems: "center" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                            {currentIssue.riskOwnerName || currentIssue.risk_owner || "Not assigned"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>

          {/* Comments Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader onClick={() => toggleSection("comments")}>
              {expandedSections.comments ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600, fontSize: "0.85rem" }}>
                Comments
              </Typography>
            </SectionHeader>
            <Collapse in={expandedSections.comments}>
              <Box sx={{ pl: 3, pb: 1 }}>
                <CommentsSection
                  issueId={currentIssue.id}
                  currentEmployeeId={currentEmployeeId}
                  issueDetails={currentIssue}
                  getCompanyId={getCompanyId}
                />
              </Box>
            </Collapse>
          </Box>
        </PanelContent>
      </PanelContainer>
    </>
  )
}

export default TaskDetailsPanel